import { api } from '../data/api.ts';
import { ensureNotifyKey, sealTitle } from './notify.ts';
import { nextOccurrence } from './parse.ts';
import { todos } from './store.ts';

/**
 * Mitteilungen für fällige Erinnerungen (docs/KONZEPT-ERINNERUNGEN.md, Abschnitt 5).
 *
 * Die Rechnung bleibt hier: Der Client löst Wiederholungen auf und meldet dem Server
 * eine Liste von Zeitpunkten samt verschlüsseltem Titel. Der Server hält keine Regel
 * nach — er kennt nur „wann", nie „was".
 *
 * Deshalb muss die Liste erneuert werden, sobald sich etwas ändert, und sie reicht
 * nur bis zum Horizont: Wer die App zwei Monate nicht öffnet, bekommt danach keine
 * Mitteilung mehr. Das ist der Preis dafür, dass der Server nichts weiß.
 */

/** Nie mehr als so viele Termine je Erinnerung vormerken. */
const MAX_PER_TODO = 12;
/** Muss zur Grenze des Servers passen. */
const MAX_ENTRIES = 200;
/** Was in weniger als einer halben Minute fällig wäre, lohnt den Umweg nicht mehr. */
const LEAD_MS = 30_000;

export type PushState = {
  supported: boolean;
  /** Der Server hat VAPID-Schlüssel. */
  available: boolean;
  permission: NotificationPermission;
  enabled: boolean;
  /** iOS liefert Mitteilungen nur an die installierte App. */
  needsInstall: boolean;
};

export function pushSupported(): boolean {
  return (
    typeof Notification !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    window.isSecureContext
  );
}

/** Auf iOS gibt es Mitteilungen nur „Zum Home-Bildschirm", nicht im Safari-Tab. */
function needsInstall(): boolean {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;
  return ios && !standalone;
}

async function subscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function pushState(): Promise<PushState> {
  const supported = pushSupported();
  if (!supported) {
    return { supported, available: false, permission: 'denied', enabled: false, needsInstall: needsInstall() };
  }
  const config = await api.pushConfig().catch(() => ({ available: false, publicKey: null, horizonDays: 60 }));
  return {
    supported,
    available: config.available,
    permission: Notification.permission,
    enabled: (await subscription()) !== null && Notification.permission === 'granted',
    needsInstall: needsInstall(),
  };
}

/** Der öffentliche VAPID-Schlüssel kommt als base64url und muss zu Bytes werden. */
function decodeKey(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

function keysOf(sub: PushSubscription): { endpoint: string; p256dh: string; auth: string } | null {
  const json = sub.toJSON();
  const p256dh = json.keys?.['p256dh'];
  const auth = json.keys?.['auth'];
  if (!p256dh || !auth) return null;
  return { endpoint: sub.endpoint, p256dh, auth };
}

export class PushUnavailable extends Error {}
export class PushDenied extends Error {}

/** Muss aus einer Geste heraus gerufen werden — der Browser verlangt es. */
export async function enablePush(): Promise<void> {
  if (!pushSupported()) throw new PushUnavailable();
  const config = await api.pushConfig();
  if (!config.available || !config.publicKey) throw new PushUnavailable();

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushDenied();

  await ensureNotifyKey();
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const sub =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeKey(config.publicKey) as BufferSource,
    }));

  const keys = keysOf(sub);
  if (!keys) throw new PushUnavailable();
  await api.pushSubscribe(keys);
  await syncSchedule();
}

export async function disablePush(): Promise<void> {
  const sub = await subscription();
  if (!sub) return;
  await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

/** Wie viele Termine beim Server liegen und wann der nächste ist. */
export async function scheduleInfo(): Promise<{ count: number; next: number | null } | null> {
  const sub = await subscription();
  if (!sub) return null;
  return api.pushScheduleInfo(sub.endpoint).catch(() => null);
}

/** Probemitteilung aus den Einstellungen. */
export async function testPush(): Promise<boolean> {
  const sub = await subscription();
  if (!sub) return false;
  await api.pushTest(sub.endpoint);
  return true;
}

async function upcoming(horizonDays: number): Promise<{ ref: string; fireAt: number; payload: string }[]> {
  const now = Date.now();
  const until = now + horizonDays * 86_400_000;
  const list = await todos.all();
  const out: { ref: string; fireAt: number; payload: string }[] = [];

  for (const todo of list) {
    if (todo.done || todo.dueAt === null) continue;
    let when = todo.dueAt;
    for (let step = 0; step < MAX_PER_TODO && when <= until; step += 1) {
      if (when > now + LEAD_MS) {
        out.push({
          // Zufällig, nicht aus der Erinnerung abgeleitet: Die Kennung soll dem
          // Server nichts verraten, auch nicht über zwei Läufe hinweg.
          ref: crypto.randomUUID(),
          fireAt: when,
          payload: await sealTitle(todo.text),
        });
      }
      if (!todo.repeat) break;
      when = nextOccurrence(when, todo.repeat, todo.tz ?? undefined);
    }
  }

  out.sort((a, b) => a.fireAt - b.fireAt);
  return out.slice(0, MAX_ENTRIES);
}

/** Meldet die Zeitpunkte neu an. Ohne Anmeldung ein stiller Nichtstuer. */
export async function syncSchedule(): Promise<number> {
  const sub = await subscription();
  if (!sub) return 0;
  const config = await api.pushConfig().catch(() => null);
  if (!config?.available) return 0;
  const entries = await upcoming(config.horizonDays);
  const result = await api.pushSchedule(sub.endpoint, entries).catch(() => null);
  return result?.stored ?? 0;
}

let pending: ReturnType<typeof setTimeout> | null = null;

/**
 * Nach einer Änderung neu anmelden, aber nicht bei jedem Tastendruck: Beim Tippen
 * einer Erinnerung feuert das CRDT im Sekundentakt.
 */
export function scheduleSoon(): void {
  if (pending) clearTimeout(pending);
  pending = setTimeout(() => {
    pending = null;
    void syncSchedule().catch(() => undefined);
  }, 5_000);
}

/** Hängt die Anmeldung an die Erinnerungen. Wird beim Entsperren gerufen. */
export function startPushSync(): () => void {
  void syncSchedule().catch(() => undefined);
  return todos.onChange(() => scheduleSoon());
}
